const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

interface ApiErrorBody {
  status: number
  message: string
  code?: string
  details?: Record<string, unknown>
}

const USER_FRIENDLY_MESSAGES: Record<string, string> = {
  INVALID_CREDENTIALS: 'Incorrect email or password. Please try again.',
  ACCOUNT_NOT_FOUND: 'No account found with that email address.',
  ACCOUNT_SUSPENDED: 'Your account has been suspended. Please contact support.',
  EMAIL_ALREADY_EXISTS: 'An account with this email already exists. Try logging in instead.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  RATE_LIMITED: 'Too many attempts. Please wait a moment and try again.',
  DOMAIN_NOT_VERIFIED: 'This domain has not been verified yet.',
  MISSING_API_KEY: 'Authentication required. Please sign in again.',
  SENDING_PAUSED: 'Sending is currently paused on your account.',
  SANDBOX_RECIPIENT_RESTRICTED: 'Sandbox mode only allows sending to your account email.',
  SANDBOX_RATE_LIMITED: 'Sandbox rate limit reached (10 emails per hour). Try again later.',
  SANDBOX_BATCH_LIMITED: 'Sandbox mode limits batch size to 1 email.',
  DOMAIN_ALREADY_EXISTS: 'This domain is already on your account. Resuming verification.',
  FORBIDDEN: 'Your API key does not have permission for this endpoint.',
  SUBSCRIPTION_REQUIRED: 'This feature requires a paid plan. Upgrade to continue.',
}

function friendlyMessage(status: number, code?: string, rawMessage?: string): string {
  if (code && code in USER_FRIENDLY_MESSAGES && USER_FRIENDLY_MESSAGES[code]) {
    return USER_FRIENDLY_MESSAGES[code]
  }
  // For VALIDATION_ERROR, pass through the raw message — it's already user-friendly.
  if (code === 'VALIDATION_ERROR' && rawMessage) {
    return rawMessage
  }
  if (status === 401) return 'Your session has expired. Please sign in again.'
  if (status === 403) return 'You don\u2019t have permission to do that.'
  if (status === 404) return 'The requested resource was not found.'
  if (status === 409) return 'This already exists. Please try a different value.'
  if (status === 422) return rawMessage ?? 'Please check your input and try again.'
  if (status === 429) return 'Too many requests. Please wait a moment and try again.'
  if (status >= 500) return 'Something went wrong on our end. Please try again in a moment.'
  return rawMessage ?? 'Something went wrong. Please try again.'
}

class ApiClientError extends Error {
  readonly status: number
  readonly code: string | undefined
  readonly details: Record<string, unknown> | undefined

  constructor({ status, message, code, details }: ApiErrorBody) {
    super(friendlyMessage(status, code, message))
    this.name = 'ApiClientError'
    this.status = status
    this.code = code
    this.details = details
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        Accept: 'application/json',
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
  } catch {
    throw new ApiClientError({
      status: 0,
      message: 'Unable to connect. Please check your internet connection and try again.',
    })
  }

  if (response.status === 401 && !path.startsWith('/auth/')) {
    // Only hard-redirect for /dashboard/ endpoints (session-only auth).
    // /v1/ endpoints use dualAuth (session OR api-key) — a 401 there may be
    // normal during onboarding (no API key yet, session race). Let TanStack
    // Query surface the error instead of nuking the page.
    if (!path.startsWith('/v1/')) {
      window.location.href = '/login'
    }
    throw new ApiClientError({ status: 401, message: 'Session expired' })
  }

  if (!response.ok) {
    let rawMessage = response.statusText
    let code: string | undefined
    let details: Record<string, unknown> | undefined
    try {
      const body = (await response.json()) as Record<string, unknown>
      const err = (body.error ?? body) as Record<string, unknown>
      rawMessage = (err.message as string) ?? rawMessage
      code = err.code as string | undefined
      details = err.details as Record<string, unknown> | undefined
    } catch {
      // response wasn't JSON
    }
    throw new ApiClientError({ status: response.status, message: rawMessage, code, details })
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const apiClient = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
}

export { ApiClientError }
